import { AnimationBuilder, RouterDirection } from '@ionic/core';

// TODO: is there a native better way to do this? href routing is not the answer, and ionic router push() is not comfortable to use
// Aquest helper aporta un workaround al fet de no poder anar de https://a/b -> https://a/b/c, sinó fer https://a/c
// També aporta un workaround per tenir un cache de dades per poder accedir desde les rutes dels components
// També permet navegar a una ruta especificant les propietats per al component al que es navega
export class RouterManager {
    // TODO: how to know the nav tied to a router?
    private router: HTMLIonRouterElement;
    private nav: HTMLIonNavElement;

    /** Get ion-router. If not initialized, search it in window.document. This way it can be created before the app-root creates the ion-router. */
    getRouter(): HTMLIonRouterElement {
        if (!this.router) {
            this.router = window.document.querySelector('ion-router');
        }
        return this.router;
    }

    getNav(): HTMLIonNavElement {
        if (!this.nav) {
            this.nav = window.document.querySelector('ion-nav');
        }
        return this.nav;
    }

    /** Get current path excluding protocol, domain and port */
    public getCurrentPath() {
        return this.getRouter().baseURI.replace(/http(s)?:\/\/[a-z0-9:\-.]+[.]*([/][#])?/, '');
    }

    /**
     * Navigates to url, saving data in cache for the specified url.
     *
     * Animations:
     * - @see https://forum.ionicframework.com/t/page-transition-direction-in-ionic-4/148518/8
     * - @see https://medium.com/nerd-for-tech/ionic-react-implementing-custom-page-transition-animation-48aa3086e9da
     *
     * @param url URL to navigate to
     * @param componentProps data to keep cached for the url
     * @param direction direction for the animation
     * @param animation
     */
    public async navigate(
        url: string,
        componentProps: { [key: string]: any } = null,
        direction: RouterDirection = 'forward',
        animation: AnimationBuilder = null,
    ) {
        // Mirar si és una navegació absoluta o relativa
        const route = url.startsWith('/') ? url : `${this.getCurrentPath()}/${url}`;
        const router = this.getRouter();

        // TODO: handle redirects...
        // Ens posem a observar el ion-nav per veure si s'ha afegit el component corresponent a la ruta definida al <ion-route>
        // que fa match amb la url indicada. Per exemple, si estem navegant a /home/1234 i tenim un ion-route definit així:
        // <ion-route url="/home/:channelId" component="channel-home" />
        // Observarem els canvis produïts al <ion-nav> i quan s'hi afegeixi un component <channel-home>
        // li sobreescriurem les seves propietats amb les indicades al componentProps
        let observer: MutationObserver;
        if (componentProps && Object.keys(componentProps).length > 0) {
            const urlSegments = url.split('/');
            // Obtenim els `component` definits pels <ion-route> que fan match amb la `url` a la que volem navegar
            const ionRouteComponents = Array.from(router.childNodes).reduce((components, ionRoute: HTMLIonRouteElement) => {
                const segments = ionRoute.url?.split('/') || [];
                // Si veiem que no tenen la mateixa longitud, directament passem al següent <ion-route> o <ion-route-redirect>
                // Mirem si la url a la que volem navegar fa match amb la ruta rel <ion-route>
                if ((segments.length === urlSegments.length)
                    && urlSegments.every((segment, idx) => (segment === segments[idx] || segments[idx].startsWith(':')))
                ) {
                    // Aquest <ion-route> té definida una url que fa match amb la url a la que volem navegar, l'afegim
                    return [...components, ionRoute.component.toLowerCase()];
                }
                return components;
            }, []);

            // Observem canvis al ion-nav per veure si s'ha afegit algun dels components que fan match amb la ruta de la url indicada
            if (ionRouteComponents.length > 0) {
                observer = new MutationObserver(m => this.updateComponentProps(m, ionRouteComponents, componentProps));
                observer.observe(this.getNav(), {
                    childList: true,
                    attributes: false,
                    subtree: false,
                    characterData: false,
                });
            }
        }

        // Naveguem a la url indicada
        let error: any;
        try {
            await router.push(route, direction, animation);
        } catch (err) {
            error = err;
        }

        // Desconnectem els observadors
        if (observer) {
            observer.disconnect();
        }

        if (error) {
            throw error;
        }
    }

    private updateComponentProps(mutations: MutationRecord[], observedTags: string[], componentProps: { [key: string]: any }) {
        // Filtrem els nodes afegits corresponents amb alguna de les tags que estem observant
        const nodes: Node[] = mutations
            .filter(m => m.type === 'childList')
            .map(m => Array.from(m.addedNodes))
            .reduce((filtered, addedNodes) => ([
                ...filtered,
                ...addedNodes.filter(an => observedTags.some(ct => ct === an.nodeName.toLowerCase())),
            ]), []);

        if (nodes.length > 0) {
            // Aplicar les propietats al component
            // eslint-disable-next-line no-param-reassign
            nodes.forEach(node => Object.keys(componentProps).forEach(k => (node[k] = componentProps[k])));
        }
    }

    /** Go back to previous page in the window.history */
    public goBack() {
        // TODO: quan es fa back després d'anar a un tab no funciona bé (per cada tab, un back que no fa res)
        return this.getRouter().back();
    }
}

const routerManager: RouterManager = new RouterManager();

export { routerManager };
